import { useMemo, useCallback, useRef } from "react";
import { useLogs } from "../context/LogsContext";

export const useBlockTestsPagination = ({
  effectivePagination,
  isSearching,
  currentSearchPageRef,
  getCurrentSearchQuery,
  searchMode,
}) => {
  const {
    queryCache,
    generateCacheKey,
    searchBlockTests,
    fetchBlockTestsForPage,
    isLoadingBlockTests,
  } = useLogs();

  // Use ref to avoid re-renders for tracking page change attempts
  const pendingPageChangeRef = useRef(false);

  // Calculate total pages
  const totalPages = useMemo(
    () => Math.ceil(effectivePagination.total / effectivePagination.limit) || 1,
    [effectivePagination.total, effectivePagination.limit]
  );

  // Handle page change with debouncing to prevent multiple calls
  const handlePageChange = useCallback(
    (newPage) => {
      if (
        newPage > 0 &&
        newPage <= totalPages &&
        !pendingPageChangeRef.current &&
        !isLoadingBlockTests
      ) {
        pendingPageChangeRef.current = true;

        // Use setTimeout to avoid potential React state batching issues
        setTimeout(() => {
          if (isSearching) {
            // Handle search pagination
            currentSearchPageRef.current = newPage;
            const currentQuery = getCurrentSearchQuery();

            let formattedQuery = currentQuery;
            if (searchMode === "organization" && currentQuery) {
              formattedQuery = parseInt(currentQuery, 10);
            }

            const cacheKey = generateCacheKey(
              newPage,
              effectivePagination.limit,
              `${searchMode}:${formattedQuery}`
            );

            const searchParams = {};
            if (searchMode === "name") {
              searchParams.name = currentQuery;
            } else if (searchMode === "organization") {
              searchParams.orgid = parseInt(currentQuery, 10);
            }

            // Pass cache flag to avoid unnecessary API calls
            searchBlockTests(
              searchParams,
              newPage,
              effectivePagination.limit,
              !!queryCache[cacheKey]
            );
          } else {
            // Regular pagination
            const cacheKey = generateCacheKey(
              newPage,
              effectivePagination.limit,
              "blockTests"
            );
            fetchBlockTestsForPage(newPage, !!queryCache[cacheKey]);
          }

          // Reset pending flag after a delay to prevent rapid multiple clicks
          setTimeout(() => {
            pendingPageChangeRef.current = false;
          }, 300);
        }, 0);
      }
    },
    [
      totalPages,
      isLoadingBlockTests,
      isSearching,
      currentSearchPageRef,
      getCurrentSearchQuery,
      searchMode,
      generateCacheKey,
      effectivePagination.limit,
      searchBlockTests,
      queryCache,
      fetchBlockTestsForPage,
    ]
  );

  // Create pagination button elements
  const pageButtons = useMemo(() => {
    const currentPage = effectivePagination.page;
    const buttons = [];

    // Add previous button
    buttons.push({
      key: "prev",
      onClick: () => handlePageChange(currentPage - 1),
      disabled: currentPage === 1 || isLoadingBlockTests,
      label: "«",
      type: "prev",
    });

    // Calculate range of page numbers to display
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + 4);

    // Adjust if we're near the end
    if (endPage - startPage < 4) {
      startPage = Math.max(1, endPage - 4);
    }

    // Add first page and ellipsis if needed
    if (startPage > 1) {
      buttons.push({
        key: 1,
        onClick: () => handlePageChange(1),
        active: currentPage === 1,
        disabled: isLoadingBlockTests,
        label: "1",
        type: "page",
      });

      if (startPage > 2) {
        buttons.push({
          key: "start-ellipsis",
          label: "...",
          type: "ellipsis",
        });
      }
    }

    // Add page number buttons
    for (let i = startPage; i <= endPage; i++) {
      buttons.push({
        key: i,
        onClick: () => handlePageChange(i),
        active: currentPage === i,
        disabled: isLoadingBlockTests,
        label: i.toString(),
        type: "page",
      });
    }

    // Add last page and ellipsis if needed
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        buttons.push({
          key: "end-ellipsis",
          label: "...",
          type: "ellipsis",
        });
      }

      buttons.push({
        key: totalPages,
        onClick: () => handlePageChange(totalPages),
        active: currentPage === totalPages,
        disabled: isLoadingBlockTests,
        label: totalPages.toString(),
        type: "page",
      });
    }

    // Add next button
    buttons.push({
      key: "next",
      onClick: () => handlePageChange(currentPage + 1),
      disabled: currentPage === totalPages || isLoadingBlockTests,
      label: "»",
      type: "next",
    });

    return buttons;
  }, [
    effectivePagination.page,
    totalPages,
    handlePageChange,
    isLoadingBlockTests,
  ]);

  return {
    totalPages,
    currentPage: effectivePagination.page,
    pageButtons,
    handlePageChange,
    showPagination: effectivePagination.total > 0,
  };
};
